import React from 'react';
import Button from '../../ui/Button';

export interface FeatureData {
  wifi: boolean;
  menu: boolean;
  punchCard: boolean;
  reviewFunnel: boolean;
  specials: boolean;
  reservations: boolean;
  socialLinks: boolean;
}

interface FeatureSelectionStepProps {
  data: FeatureData;
  onUpdate: (data: FeatureData) => void;
  onNext: () => void;
  onBack: () => void;
}

const FEATURES: { key: keyof FeatureData; label: string; description: string; locked?: boolean }[] = [
  { key: 'wifi', label: 'Wi-Fi access', description: 'Gate your Wi-Fi behind a quick survey to learn about your customers.', locked: true },
  { key: 'menu', label: 'Digital menu', description: 'Share your menu via QR code — link to a URL, upload a PDF, or build one in-app.' },
  { key: 'punchCard', label: 'Punch card', description: 'Digital loyalty card that tracks visits and rewards returning customers.' },
  { key: 'reviewFunnel', label: 'Review funnel', description: 'Prompt first-time visitors to leave a Google or Yelp review after connecting.' },
  { key: 'specials', label: 'Daily specials', description: 'Display today\'s specials on your guest page.' },
  { key: 'reservations', label: 'Reservations', description: 'Link to your reservation system (OpenTable, Resy, etc.).' },
  { key: 'socialLinks', label: 'Social links', description: 'Show your social media links on the guest page.' },
];

const FeatureSelectionStep: React.FC<FeatureSelectionStepProps> = ({
  data,
  onUpdate,
  onNext,
  onBack,
}) => {
  const toggleFeature = (key: keyof FeatureData) => {
    if (key === 'wifi') return; // Wi-Fi is always on
    onUpdate({ ...data, [key]: !data[key] });
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-1.5">Step 2 of 5</div>
        <h2 className="text-display text-2xl text-coffee-900">Choose features</h2>
        <p className="mt-1.5 text-sm text-coffee-600">
          Pick what you want to offer guests. You can change this any time.
        </p>
      </div>

      <div className="space-y-2">
        {FEATURES.map((feature) => (
          <label
            key={feature.key}
            className={`flex items-start gap-3 p-4 rounded-xl border transition-colors ${
              feature.locked ? 'cursor-default' : 'cursor-pointer'
            } ${
              data[feature.key]
                ? 'border-coffee-300 bg-coffee-50'
                : 'border-coffee-100 bg-white hover:border-coffee-200'
            }`}
          >
            <input
              type="checkbox"
              checked={data[feature.key]}
              onChange={() => toggleFeature(feature.key)}
              disabled={feature.locked}
              className="mt-0.5 h-4 w-4 rounded border-coffee-300 text-coffee-600 focus:ring-coffee-500 disabled:opacity-70"
              aria-label={feature.label}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-coffee-900">{feature.label}</span>
                {feature.locked && (
                  <span className="px-2 py-0.5 text-[10px] tracking-eyebrow font-semibold bg-coffee-100 text-coffee-600 rounded-full">
                    Required
                  </span>
                )}
              </div>
              <p className="text-xs text-coffee-500 mt-0.5 leading-relaxed">
                {feature.description}
              </p>
            </div>
          </label>
        ))}
      </div>

      <div className="flex justify-between pt-2 border-t border-coffee-100">
        <Button variant="ghost" onClick={onBack}>Back</Button>
        <Button variant="primary" onClick={onNext}>Next</Button>
      </div>
    </div>
  );
};

export default FeatureSelectionStep;
